/**
 * Building a form, and editing a draft.
 *
 * Fields are added one at a time and can be reordered; each field's
 * settings follow its type (options for a choice, bounds for a number,
 * lengths for text). The legal statement is required — it is what the
 * person signs.
 */

import { useId, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useTaxonomy } from '../admin/useTaxonomy';
import { useMembers } from '../admin/useMembers';
import { ASSIGNABLE_ROLES, ROLE_DISPLAY_NAMES } from '../../types/roles';
import { isCallFailure } from '../../lib/callable';
import {
  FORM_DESCRIPTION_MAX, FORM_FIELD_DESCRIPTION_MAX, FORM_FIELD_LABEL_MAX, FORM_FIELDS_MAX, FORM_LONG_TEXT_MAX,
  FORM_OPTION_MAX, FORM_OPTIONS_MAX, FORM_RENEWAL_MONTHS_MAX, FORM_TITLE_MAX,
} from '../../types/fieldLimits';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { BackLink } from '../../shared/BackLink';
import { CharCount } from '../../shared/CharCount';
import { ListInput } from '../../shared/ListInput';
import { SegmentedChoice } from '../../shared/SegmentedChoice';
import { useCreateForm, useForm, useUpdateForm } from './useForms';
import {
  AUDIENCE_LABEL, AUDIENCES, CHOICE_TYPES, EMPTY_FORM, FIELD_TYPE_HINT, FIELD_TYPE_LABEL, FIELD_TYPES, FORM_CATEGORIES, FORM_CATEGORY_LABEL,
  formProblem, newField, toDraft, type FieldDraft, type FieldType, type FormFields, type FormRecord,
} from './types';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './Forms.module.css';

export const NOT_DRAFT_TITLE = 'Only a draft can be edited';

function fieldsFrom(r: FormRecord): FormFields {
  return {
    title: r.title, description: r.description ?? '', instructions: r.instructions ?? '',
    fields: r.fields.map(toDraft), targetAudience: r.targetAudience, targetDepartmentIds: r.targetDepartmentIds,
    targetRoles: r.targetRoles, targetUserIds: r.targetUserIds, requiresLegalAttestation: r.requiresLegalAttestation,
    legalAttestationText: r.legalAttestationText,
    formCategory: r.formCategory, renewalIntervalMonths: r.renewalIntervalMonths === null ? '' : String(r.renewalIntervalMonths),
  };
}

export function FormEditPage() {
  const { formId } = useParams();
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const editing = Boolean(formId);
  const record = useForm(orgId, formId ?? null, editing);

  if (!editing) return <FormBuilder orgId={orgId} />;
  if (record.isLoading) return <Spinner label="Opening the form" />;
  if (record.error || !record.data) {
    return <div className={styles.body}><PageState title="Could not open the form" action={{ to: '/forms', label: 'Back to forms' }} tone="alert">{isCallFailure(record.error) ? record.error.message : 'It may have been removed.'}</PageState></div>;
  }
  if (record.data.status !== 'draft') {
    return (
      <div className={styles.body}>
        <PageState title={NOT_DRAFT_TITLE} action={{ to: `/forms/${formId}`, label: 'Back to the form' }}>
          A published form is what people signed; changing it underneath them would change the record. Publish a new version from its page instead.
        </PageState>
      </div>
    );
  }
  return <FormBuilder orgId={orgId} record={record.data} />;
}

function FormBuilder({ orgId, record }: { orgId: string | null; record?: FormRecord }) {
  const navigate = useNavigate();
  const create = useCreateForm(orgId);
  const update = useUpdateForm(orgId, record?.id ?? '');
  const departments = useTaxonomy('department', Boolean(orgId));
  const members = useMembers(orgId);
  const [f, setF] = useState<FormFields>(() => (record ? fieldsFrom(record) : EMPTY_FORM));
  const [touched, setTouched] = useState(false);
  const [newType, setNewType] = useState<FieldType>('shortText');
  const ids = useId();
  const id = (n: string) => `${ids}-${n}`;
  const set = <K extends keyof FormFields>(k: K, v: FormFields[K]) => setF((c) => ({ ...c, [k]: v }));
  const setField = (i: number, patch: Partial<FieldDraft>) => set('fields', f.fields.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const move = (i: number, by: number) => {
    const next = [...f.fields]; const j = i + by;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j]!, next[i]!];
    set('fields', next);
  };
  const toggle = (k: 'targetDepartmentIds' | 'targetRoles' | 'targetUserIds', v: string) =>
    set(k, f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v]);

  const problem = formProblem(f);
  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const backTo = record ? `/forms/${record.id}` : '/forms';

  function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (problem) return;
    if (record) update.mutate(f, { onSuccess: () => navigate(backTo) });
    else create.mutate(f, { onSuccess: (newId) => navigate(`/forms/${newId}`) });
  }

  return (
    <>
      <header className={styles.header}>
        <div className={styles.titled}>
          {record && <p className={styles.eyebrow}>Form · Draft</p>}
          <div className={styles.titleLine}>
            <BackLink to={backTo} label={record ? 'Back to the form' : 'Back to forms'} />
            <h1 className={styles.title}>{record ? `Edit ${record.title}` : 'New form'}</h1>
          </div>
          <p className={styles.subtitle}>{record ? 'Changes are saved to this draft.' : 'Ask the questions, choose who must answer, and set the statement they sign. It starts as a draft; publishing assigns it.'}</p>
        </div>
      </header>

      <div className={styles.body}>
        <form className={form.form} onSubmit={submit} noValidate style={{ marginTop: 0, maxWidth: 780 }}>
          {error && <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">{isCallFailure(error) ? error.message : 'The form could not be saved.'}</div>}
          {touched && problem && <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">{problem}</div>}

          <div className={form.field}>
            <label className={form.label} htmlFor={id('title')}>Title</label>
            <input id={id('title')} className={form.input} type="text" required maxLength={FORM_TITLE_MAX} value={f.title}
              onChange={(e) => set('title', e.target.value.slice(0, FORM_TITLE_MAX))} autoComplete="off"
              aria-invalid={(touched && !f.title.trim()) || undefined} />
            <CharCount value={f.title} max={FORM_TITLE_MAX} />
          </div>
          <div className={form.field}>
            <label className={form.label} htmlFor={id('desc')}>Description <span className={form.optional}>(optional)</span></label>
            <textarea id={id('desc')} className={form.textarea} rows={3} maxLength={FORM_DESCRIPTION_MAX} value={f.description} onChange={(e) => set('description', e.target.value.slice(0, FORM_DESCRIPTION_MAX))} />
            <CharCount value={f.description} max={FORM_DESCRIPTION_MAX} />
          </div>
          <div className={form.field}>
            <label className={form.label} htmlFor={id('instr')}>Instructions <span className={form.optional}>(optional)</span></label>
            <textarea id={id('instr')} className={form.textarea} rows={3} maxLength={FORM_LONG_TEXT_MAX} value={f.instructions} onChange={(e) => set('instructions', e.target.value.slice(0, FORM_LONG_TEXT_MAX))} aria-describedby={id('instr-hint')} />
            <CharCount value={f.instructions} max={FORM_LONG_TEXT_MAX} />
            <p className={form.hint} id={id('instr-hint')}>Shown above the questions.</p>
          </div>

          <div className={form.row}>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('cat')}>Record type</label>
              <select id={id('cat')} className={form.select} value={f.formCategory} onChange={(e) => set('formCategory', e.target.value as FormFields['formCategory'])} aria-describedby={id('cat-hint')}>
                {FORM_CATEGORIES.map((c) => <option key={c} value={c}>{FORM_CATEGORY_LABEL[c]}</option>)}
              </select>
              <p className={form.hint} id={id('cat-hint')}>What kind of record the signed copy is. A surveyor asks for them by kind.</p>
            </div>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('renew')}>Sign again every <span className={form.optional}>(months, optional)</span></label>
              <input id={id('renew')} className={form.input} type="number" min={1} max={FORM_RENEWAL_MONTHS_MAX} step={1} value={f.renewalIntervalMonths}
                onChange={(e) => set('renewalIntervalMonths', e.target.value)} aria-describedby={id('renew-hint')} style={{ maxWidth: 200 }} />
              <p className={form.hint} id={id('renew-hint')}>Annual forms are 12. Leave empty for a one-time signing. Publishing a new version reassigns it.</p>
            </div>
          </div>

          <h2 className={form.group}>Fields</h2>
          {f.fields.length === 0 && <p className={`${form.hint} ${form.groupHint}`}>No fields yet. Choose a type and add the first.</p>}
          <ol className={own.fieldList}>
            {f.fields.map((d, i) => (
              <li key={d.id} className={own.fieldCard}>
                <div className={own.fieldHead}>
                  <span className={own.fieldNo}>{i + 1}</span>
                  <span>{FIELD_TYPE_LABEL[d.type]}</span>
                  <div className={own.fieldTools}>
                    <button type="button" className={own.tool} onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move field ${i + 1} up`}>↑</button>
                    <button type="button" className={own.tool} onClick={() => move(i, 1)} disabled={i === f.fields.length - 1} aria-label={`Move field ${i + 1} down`}>↓</button>
                    <button type="button" className={own.tool} onClick={() => set('fields', f.fields.filter((_, j) => j !== i))} aria-label={`Remove field ${i + 1}`}>×</button>
                  </div>
                </div>
                <div className={own.fieldGrid}>
                  <div className={own.full}>
                    <label className={form.label} htmlFor={id(`l-${d.id}`)}>Label</label>
                    <input id={id(`l-${d.id}`)} className={form.input} type="text" required maxLength={FORM_FIELD_LABEL_MAX} value={d.label}
                      onChange={(e) => setField(i, { label: e.target.value.slice(0, FORM_FIELD_LABEL_MAX) })} aria-invalid={(touched && !d.label.trim()) || undefined} autoComplete="off" />
                    <CharCount value={d.label} max={FORM_FIELD_LABEL_MAX} />
                  </div>
                  <div className={own.full}>
                    <label className={form.label} htmlFor={id(`d-${d.id}`)}>Description <span className={form.optional}>(optional)</span></label>
                    <input id={id(`d-${d.id}`)} className={form.input} type="text" maxLength={FORM_FIELD_DESCRIPTION_MAX} value={d.description}
                      onChange={(e) => setField(i, { description: e.target.value.slice(0, FORM_FIELD_DESCRIPTION_MAX) })} autoComplete="off" />
                  </div>
                  <div>
                    <label className={form.label} htmlFor={id(`t-${d.id}`)}>Type</label>
                    <select id={id(`t-${d.id}`)} className={form.select} value={d.type} onChange={(e) => setField(i, { type: e.target.value as FieldType })} aria-describedby={id(`th-${d.id}`)}>
                      {FIELD_TYPES.map((t) => <option key={t} value={t}>{FIELD_TYPE_LABEL[t]}</option>)}
                    </select>
                    <p className={form.hint} id={id(`th-${d.id}`)}>{FIELD_TYPE_HINT[d.type]}</p>
                  </div>
                  <div>
                    <label className={own.inlineCheck}><input type="checkbox" checked={d.required} onChange={(e) => setField(i, { required: e.target.checked })} />Required</label>
                  </div>
                  {CHOICE_TYPES.includes(d.type) && (
                    <div className={own.full}>
                      <span className={form.label} id={id(`o-${d.id}`)}>Options</span>
                      <ListInput id={id(`oi-${d.id}`)} labelledBy={id(`o-${d.id}`)} value={d.options} onChange={(v) => setField(i, { options: v })} max={FORM_OPTIONS_MAX} itemMax={FORM_OPTION_MAX} placeholder="Type an option and press Enter" />
                    </div>
                  )}
                  {d.type === 'number' && (
                    <>
                      <div><label className={form.label} htmlFor={id(`min-${d.id}`)}>Minimum <span className={form.optional}>(optional)</span></label><input id={id(`min-${d.id}`)} className={form.input} type="number" value={d.minValue} onChange={(e) => setField(i, { minValue: e.target.value })} /></div>
                      <div><label className={form.label} htmlFor={id(`max-${d.id}`)}>Maximum <span className={form.optional}>(optional)</span></label><input id={id(`max-${d.id}`)} className={form.input} type="number" value={d.maxValue} onChange={(e) => setField(i, { maxValue: e.target.value })} /></div>
                    </>
                  )}
                  {(d.type === 'shortText' || d.type === 'longText') && (
                    <>
                      <div><label className={form.label} htmlFor={id(`minl-${d.id}`)}>Shortest <span className={form.optional}>(characters, optional)</span></label><input id={id(`minl-${d.id}`)} className={form.input} type="number" min={0} value={d.minLength} onChange={(e) => setField(i, { minLength: e.target.value })} /></div>
                      <div><label className={form.label} htmlFor={id(`maxl-${d.id}`)}>Longest <span className={form.optional}>(characters, optional)</span></label><input id={id(`maxl-${d.id}`)} className={form.input} type="number" min={1} value={d.maxLength} onChange={(e) => setField(i, { maxLength: e.target.value })} /></div>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ol>
          <div className={styles.actions} style={{ marginTop: 'var(--sp-4)', marginBottom: 'var(--sp-6)' }}>
            <label className="vh" htmlFor={id('newtype')}>Type of field to add</label>
            <select id={id('newtype')} className={form.select} style={{ width: 'auto' }} value={newType} onChange={(e) => setNewType(e.target.value as FieldType)}>
              {FIELD_TYPES.map((t) => <option key={t} value={t}>{FIELD_TYPE_LABEL[t]}</option>)}
            </select>
            <button type="button" className={styles.btn} disabled={f.fields.length >= FORM_FIELDS_MAX} onClick={() => set('fields', [...f.fields, newField(newType)])}>Add field</button>
          </div>

          <h2 className={form.group}>Who must complete it</h2>
          <SegmentedChoice legend="Assigned to" name={id('aud')} value={f.targetAudience} onChange={(v) => set('targetAudience', v)} options={AUDIENCES.map((a) => ({ value: a, label: AUDIENCE_LABEL[a] }))} />
          {f.targetAudience === 'departments' && (
            <fieldset className={form.field} style={{ border: 0, padding: 0, marginTop: 'var(--sp-4)' }}><legend className={form.label}>Departments</legend>
              <ul className={own.checks}>{(departments.data ?? []).filter((d) => d.isActive || f.targetDepartmentIds.includes(d.id)).map((d) => (
                <li key={d.id}><label className={own.check}><input type="checkbox" checked={f.targetDepartmentIds.includes(d.id)} onChange={() => toggle('targetDepartmentIds', d.id)} />{d.name}</label></li>
              ))}</ul></fieldset>
          )}
          {f.targetAudience === 'roles' && (
            <fieldset className={form.field} style={{ border: 0, padding: 0, marginTop: 'var(--sp-4)' }}><legend className={form.label}>Roles</legend>
              <ul className={own.checks}>{ASSIGNABLE_ROLES.map((r) => (
                <li key={r}><label className={own.check}><input type="checkbox" checked={f.targetRoles.includes(r)} onChange={() => toggle('targetRoles', r)} />{ROLE_DISPLAY_NAMES[r]}</label></li>
              ))}</ul></fieldset>
          )}
          {f.targetAudience === 'users' && (
            <fieldset className={form.field} style={{ border: 0, padding: 0, marginTop: 'var(--sp-4)' }}><legend className={form.label}>People</legend>
              {members.isLoading ? <p className={form.hint}>Loading people…</p> : (
                <ul className={own.checks}>{(members.data ?? []).filter((m) => m.status === 'active' || f.targetUserIds.includes(m.uid)).map((m) => (
                  <li key={m.uid}><label className={own.check}><input type="checkbox" checked={f.targetUserIds.includes(m.uid)} onChange={() => toggle('targetUserIds', m.uid)} />{m.displayName || m.email}</label></li>
                ))}</ul>
              )}</fieldset>
          )}

          <h2 className={form.group}>What they sign</h2>
          <label className={form.check} style={{ marginBottom: 'var(--sp-3)' }}>
            <input type="checkbox" checked={f.requiresLegalAttestation} onChange={(e) => set('requiresLegalAttestation', e.target.checked)} />
            <span>Require a legal attestation. The person must confirm the statement below to submit.</span>
          </label>
          <div className={form.field}>
            <label className={form.label} htmlFor={id('legal')}>Attestation statement</label>
            <textarea id={id('legal')} className={form.textarea} rows={4} required maxLength={FORM_LONG_TEXT_MAX} value={f.legalAttestationText}
              onChange={(e) => set('legalAttestationText', e.target.value.slice(0, FORM_LONG_TEXT_MAX))} aria-invalid={(touched && !f.legalAttestationText.trim()) || undefined} aria-describedby={id('legal-hint')} />
            <CharCount value={f.legalAttestationText} max={FORM_LONG_TEXT_MAX} />
            <p className={form.hint} id={id('legal-hint')}>Kept with every signed copy exactly as it read at the time.</p>
          </div>

          <div className={styles.actions}>
            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={pending}>{pending ? 'Saving…' : record ? 'Save changes' : 'Create form'}</button>
            <Link to={backTo} className={styles.btn}>Cancel</Link>
          </div>
        </form>
      </div>
    </>
  );
}
